"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, Checkbox, Grid, TextField } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { postWebBmbyLead } from "api"
import dayjs from "dayjs"
import { useLocale, useTranslations } from "next-intl"
import { LegacyRef, useEffect, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import {
  ArrowDown,
  CheckboxIcon,
  CheckedCheckboxIcon,
  CircleXIcon,
  NewText,
  RentalHousingFormErrorIcon,
  RentalHousingFormSuccessIcon,
} from "ui"
import { sharedColors as colors } from "ui/theme/colors"
import { submitClick, submitError, submitSuccess, useOutsideClick } from "utils"
import { generateRentalHousingFormSchema } from "utils/Form/FormSchema"
import * as yup from "yup"
import { ctaMessage } from "../../../Contact/ContactForm"
import CustomHelperText from "../../../Contact/CustomHelperText"
import { ProjectsMessages } from "../../Projects"

const FormDropdownContainer = styled.div`
  position: relative;
  width: 100%;
`

const FormDropdownTrigger = styled.div<{ $open?: boolean; $error?: boolean }>`
  display: flex;
  height: 56px;
  padding: 8px 12px;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border: 1.2px solid ${(props) => (props.$error ? "red" : "black")};
  border-radius: ${(props) => (props.$open ? "4px 4px 0 0" : "4px")};
  background: white;
`

const DropdownLabel = styled.span`
  font-size: 14px;
  color: #6f6f6f;
  line-height: normal;
`

const DropdownInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-size: 18px;
  color: black;
  background: transparent;
  text-align: inherit;
  &::placeholder {
    color: black;
  }
`

const FormDropdownMenu = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 200px;
  z-index: 10;
  overflow-y: auto;
  border-radius: 0 0 8px 8px;
  border: 1px solid black;
  border-top: none;
  background: white;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.16);
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 200px;
    background: black;
    width: 4px;
  }
`

const FormDropdownItem = styled.div`
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  font-size: 18px;
  line-height: 24px;
  border-bottom: 1px solid #6f6f6f;
  background: white;
  cursor: pointer;
  &:hover {
    background: #f7f7f7;
  }
  &:last-child {
    border-bottom: none;
  }
`

// BMBY lead ids are pinned here rather than read from the CMS leadFormBMBYId
// property, because the lookup keyed off projectTitle and those titles differ
// between environments, so a lead could be filed under the wrong project.
// `name` is what the dropdown shows; `aliases` are the CMS projectTitle values
// that reach us as preSelectedProject, which the display name no longer equals.
type RentalProject = { name: string; bmbyId: number; aliases: string[] }

const RENTAL_PROJECTS: Record<string, RentalProject[]> = {
  he: [
    {
      name: "אשטרום קריית היובל",
      bmbyId: 12147,
      aliases: ["קריית היובל", "קריית היובל - השכרה", "Kiryat Hayovel 4 Rent"],
    },
    { name: "אשטרום המשתלה", bmbyId: 12146, aliases: ["המשתלה", "HaMishtala"] },
    {
      name: "אשטרום נווה עופר",
      bmbyId: 12148,
      aliases: [`הגדנ"ע, נווה עופר`, "נווה עופר", "Neveh Ofer"],
    },
    { name: "אשטרום נאות פרס", bmbyId: 12145, aliases: ["נאות פרס", "Neot Peres"] },
    { name: "אשטרום נווה איילון", bmbyId: 12149, aliases: ["נווה איילון", "Ramat Pinkas"] },
  ],
  en: [
    {
      name: "Ashtrom Kiryat HaYovel",
      bmbyId: 12147,
      aliases: ["Kiryat HaYovel", "Kiryat HaYovel - For rent"],
    },
    { name: "Ashtrom HaMishtala", bmbyId: 12146, aliases: ["Hamishtala", "HaMishtala"] },
    {
      name: "Ashtrom Neve Ofer",
      bmbyId: 12148,
      aliases: ["HaGadna, Neve Ofer", "Neveh Ofer", "Neve Ofer"],
    },
    { name: "Ashtrom Neot Peres", bmbyId: 12145, aliases: ["Neot Peres"] },
    { name: "Ashtrom Neve Ayalon", bmbyId: 12149, aliases: ["Neve Ayalon", "Neva Ayalon"] },
  ],
}

const ALL_RENTAL_PROJECTS = Object.values(RENTAL_PROJECTS).flat()

// CMS titles reach us padded and inconsistently cased often enough that the repo
// already works around it elsewhere, and preSelectedProject falls back to the
// node name or the other culture's value when a variant is empty. Ids are
// locale-independent, so match against every locale's names and aliases.
const normalizeProjectName = (name: string) => name.trim().toLowerCase()

const matchRentalProject = (name: string) => {
  const target = normalizeProjectName(name)
  return ALL_RENTAL_PROJECTS.find(
    (p) =>
      normalizeProjectName(p.name) === target ||
      p.aliases.some((alias) => normalizeProjectName(alias) === target),
  )
}

// A CMS value is only usable as a BMBY id if it parses to a positive number;
// anything else must fall through to bmbyId rather than post "NaN".
const toBmbyId = (raw: unknown): number | undefined => {
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

const CHARACTER_LIMIT = 500

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

export type RentalHousingFormProps = {
  onClose: () => void
  messages: ProjectsMessages
  bmbyId?: number
  companyId?: number
  emailForLead?: string
  projects: { name: string; id: number; rooms: string[] }[]
  cmsProjectLeadIds?: Record<string, number>
  preSelectedProject?: string
  preSelectedRooms?: string[]
  noAvailableApartements?: boolean
}

// Reuse same styling as Form.tsx (ContactForm)
const FormContainer = styled.div`
  --text-black: #011727;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  border: 2px solid ${(props) => props.theme.colors.propBlue};
  background: white;
  width: 100%;
  overflow: hidden;
  max-height: 90vh;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 660px;
  }
  .MuiFormHelperText-root {
    bottom: -25px;
    line-height: 1;
  }
  .MuiFormControl-root {
    will-change: transform;
  }
  .MuiFilledInput-root {
    transform: translateZ(0);
  }
  .MuiInputBase-input {
    position: relative;
    transform: translateZ(0);
    caret-color: auto;
  }
  .MuiInputLabel-root {
    color: black;
  }
`

const StyledHeader = styled.div`
  height: 84px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.colors.propBlue};
  border-radius: 10px 10px 0 0;
  padding: 16px;
  flex-shrink: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 24px;
  }
`

const StyledClose = styled(Button)`
  margin: 0;
  padding: 0;
  height: 24px;
  width: 24px;
  min-width: 0;
`

const StyledInsideForm = styled.div`
  padding: 24px;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  flex: 1;
  min-height: 0;
`

const StyledTitle = styled.div`
  margin-bottom: 16px;
`

const BottomContainer = styled.div`
  border-top: 1px solid black;
  padding: 24px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-end;
  flex-shrink: 0;
`

const StyledSubmitButton = styled(Button)`
  width: 153px;
`

const StyledCheckbox = styled(Checkbox)`
  padding: 1px 10px 0px 0;
`

const StyledCheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
`

const CheckBoxesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`

const FormStateContainer = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 400px;
  text-align: center;
  ${(props) => props.theme.breakpoints.down("md")} {
    min-height: calc(90vh - 72px);
  }
`

const FormStateTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FormStateButton = styled(Button)`
  border-radius: 200px;
  border: 2px solid black;
  font-size: 18px;
  font-weight: 600;
  padding: 8px 16px;
  height: 48px;
  min-width: 162px;
  color: black;
`

const EntryDateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EntryDateToggle = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  background: #f7f7f7;
  border-radius: 200px;
  padding: 8px;
  overflow: hidden;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: fit-content;
  }
`

const EntryDateOption = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 200px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 18px;
  line-height: 24px;
  background: ${(props) => (props.$active ? "white" : "transparent")};
  color: ${(props) => (props.$active ? "black" : "#6f6f6f")};
  padding: ${(props) => (props.$active ? "8px 30px" : "8px 16px")};
  flex: 1;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex: unset;
    padding: 8px 30px;
  }
`

function RentalHousingForm({
  onClose,
  messages,
  bmbyId,
  companyId,
  emailForLead,
  projects,
  cmsProjectLeadIds,
  preSelectedProject,
  preSelectedRooms,
  noAvailableApartements,
}: RentalHousingFormProps) {
  const [formIsSubmitted, setFormIsSubmitted] = useState(false)
  const [submitState, setSubmitState] = useState<"success" | "fail">("fail")
  const reCaptchaRef = useRef<ReCAPTCHA | null>()
  const locale = useLocale()
  const schema = generateRentalHousingFormSchema(locale === "he" ? "he" : "en")
  const t = useTranslations("ProjectsPage")
  const tForm = useTranslations("RentalHousingForm")
  const [checkedMarketing, setCheckedMarketing] = useState<boolean>(false)
  const [checkedCommercial, setCheckedCommercial] = useState<boolean>(false)

  const FORM_ELEMENT_NAME = "טופס השכרה – פרטים אישיים"
  const FORM_ERROR_NAME = "לא כל הפרטים מולאו"

  const rentalProjects = RENTAL_PROJECTS[locale] || RENTAL_PROJECTS.he
  // The drawer preselects by CMS projectTitle, so map it onto the display name.
  // Resolve across locales, then come back to this locale's label via the id.
  const preSelectedMatch = preSelectedProject ? matchRentalProject(preSelectedProject) : undefined
  const preSelectedProjectName = preSelectedMatch
    ? rentalProjects.find((p) => p.bmbyId === preSelectedMatch.bmbyId)?.name || preSelectedProject
    : preSelectedProject || ""

  type FormInput = yup.InferType<typeof schema>
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitted, isSubmitting },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      project: preSelectedProjectName,
      rooms: preSelectedRooms || [],
      entryDateType: "immediate" as const,
      specificDate: "",
      notes: "",
    },
  })

  const selectedProject = watch("project")
  const entryDateType = watch("entryDateType")
  const selectedRooms = watch("rooms") || []
  const rentalProjectNames = rentalProjects.map((p) => p.name)
  const resolveProjectId = (name: string): number => {
    const pinned = matchRentalProject(name)
    if (pinned) return pinned.bmbyId
    const fromProjects = toBmbyId(projects.find((p) => p.name === name)?.id)
    if (fromProjects) return fromProjects
    return toBmbyId(cmsProjectLeadIds?.[name]) ?? -1
  }
  const availableRooms = ["2", "2.5", "3", "3.5", "4", "4.5", "5", "5.5", "6"]
  const roomSuffix = locale === "he" ? "חדרים" : "Rooms"

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false)
  const [roomsDropdownOpen, setRoomsDropdownOpen] = useState(false)
  const [dateOpen, setDateOpen] = useState(false)
  const [projectSearch, setProjectSearch] = useState("")
  const [roomsSearch, setRoomsSearch] = useState("")
  const filteredProjects = rentalProjectNames.filter((name) =>
    name.toLowerCase().includes(projectSearch.toLowerCase()),
  )
  const filteredRooms = availableRooms.filter((r) => r.includes(roomsSearch))
  const projectDropdownRef = useRef<HTMLDivElement>(null)
  const roomsDropdownRef = useRef<HTMLDivElement>(null)
  const dateAnchorRef = useRef<HTMLDivElement>(null)

  useOutsideClick([projectDropdownRef], () => {
    setProjectDropdownOpen(false)
    setProjectSearch("")
  })
  useOutsideClick([roomsDropdownRef], () => {
    setRoomsDropdownOpen(false)
    setRoomsSearch("")
  })

  const prevProjectRef = useRef(selectedProject)
  useEffect(() => {
    if (prevProjectRef.current === selectedProject) return
    prevProjectRef.current = selectedProject
    setValue("rooms", [])
  }, [selectedProject, setValue])

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    const resolvedId = resolveProjectId(data.project)
    const projectId = resolvedId !== -1 ? resolvedId : bmbyId || -1

    reCaptchaRef?.current?.reset()
    const recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string

    if (!recaptchaResponse) return
    try {
      const senddata = {
        projectId: String(projectId),
        name: "",
        email: data.email,
        phone: data.phone,
        comments: data.notes || "",
        gRecaptchaResponse: recaptchaResponse,
        projectStatus: "MarketingAndRental" as string,
        allowedMarketing: checkedMarketing,
        allowedSmsAndEmail: checkedCommercial,
        companyId: companyId || -1,
        uri: window?.location?.href,
        toEmail: emailForLead || "",
        fname: data.firstName,
        lname: data.lastName,
        projectw: data.project,
        dateEnt:
          data.entryDateType === "immediate" || data.entryDateType === "flexible"
            ? new Date().toISOString().split("T")[0]
            : data.specificDate || undefined,
        dirat: data.rooms?.join(", ") || "",
        ...(noAvailableApartements ? { noAvailableApartements: true } : {}),
      }
      const response = await postWebBmbyLead(senddata)
      if (response?.data?.body?.addLeadSucceeded) {
        submitSuccess("שליחה", FORM_ELEMENT_NAME, checkedCommercial, checkedMarketing)
        setSubmitState("success")
        setFormIsSubmitted(true)
      } else {
        setSubmitState("fail")
        setFormIsSubmitted(true)
        submitError(
          "שליחה",
          FORM_ELEMENT_NAME,
          response?.data?.body?.errorMessage ?? FORM_ERROR_NAME,
        )
      }
    } catch (error) {
      setSubmitState("fail")
      setFormIsSubmitted(true)
      submitError("שליחה", FORM_ELEMENT_NAME, (error as any)?.message ?? FORM_ERROR_NAME)
    }
  }

  return (
    <FormContainer>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <StyledHeader>
          <NewText
            component="p"
            desktopFontSize={32}
            desktopLineHeight="36px"
            mobileFontSize={24}
            mobileLineHeight="28px"
            color={colors.white}
          >
            {tForm("formTitle")}
          </NewText>
          <StyledClose onClick={onClose}>
            <CircleXIcon />
          </StyledClose>
        </StyledHeader>

        {formIsSubmitted ? (
          submitState === "fail" ? (
            <FormStateContainer>
              <RentalHousingFormErrorIcon style={{ width: 60, height: 60 }} />
              <FormStateTextGroup>
                <NewText
                  component="p"
                  desktopFontSize={32}
                  desktopLineHeight="130%"
                  mobileFontSize={24}
                  mobileLineHeight="130%"
                  color={colors.black}
                >
                  {tForm("errorTitle")}
                </NewText>
                <NewText
                  component="p"
                  desktopFontSize={20}
                  desktopLineHeight="150%"
                  mobileFontSize={18}
                  mobileLineHeight="150%"
                  color={colors.black}
                  dangerouslySetInnerHTML={{ __html: tForm("errorMsg") }}
                />
              </FormStateTextGroup>
              <FormStateButton variant="outlined" onClick={() => setFormIsSubmitted(false)}>
                {tForm("retryBtn")}
              </FormStateButton>
            </FormStateContainer>
          ) : (
            <FormStateContainer>
              <RentalHousingFormSuccessIcon style={{ width: 60, height: 60 }} />
              <NewText
                component="p"
                desktopFontSize={20}
                desktopLineHeight="normal"
                mobileFontSize={18}
                mobileLineHeight="normal"
                color={colors.black}
                style={{ textAlign: "center", maxWidth: 400 }}
              >
                {tForm("successMsg")}
              </NewText>
              <FormStateButton variant="outlined" onClick={() => onClose()}>
                {tForm("closeBtn")}
              </FormStateButton>
            </FormStateContainer>
          )
        ) : (
          <>
            <StyledInsideForm>
              <StyledTitle>
                <NewText
                  component="p"
                  desktopFontSize={16}
                  desktopLineHeight="20px"
                  mobileFontSize={14}
                  mobileLineHeight="14px"
                  color={colors.black}
                >
                  {messages.required}
                </NewText>
              </StyledTitle>
              <Grid container columnSpacing={2} rowSpacing={4}>
                {/* First Name */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="filled"
                        label={tForm("firstName")}
                        error={!!errors.firstName}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.firstName}
                            text={errors.firstName?.message}
                          />
                        }
                        fullWidth
                        inputProps={{
                          style: { fontSize: 16 },
                        }}
                      />
                    )}
                  />
                </Grid>
                {/* Last Name */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="filled"
                        label={tForm("lastName")}
                        error={!!errors.lastName}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.lastName}
                            text={errors.lastName?.message}
                          />
                        }
                        fullWidth
                        inputProps={{
                          style: { fontSize: 16 },
                        }}
                      />
                    )}
                  />
                </Grid>
                {/* Phone */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="filled"
                        label={tForm("phone")}
                        error={!!errors.phone}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.phone}
                            text={errors.phone?.message}
                          />
                        }
                        fullWidth
                        inputProps={{
                          style: { fontSize: 16 },
                        }}
                      />
                    )}
                  />
                </Grid>
                {/* Email */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="filled"
                        label={tForm("email")}
                        error={!!errors.email}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.email}
                            text={errors.email?.message}
                          />
                        }
                        fullWidth
                        inputProps={{
                          style: { fontSize: 16 },
                        }}
                      />
                    )}
                  />
                </Grid>
                {/* Project Selection */}
                <Grid item xs={12} md={6}>
                  <FormDropdownContainer ref={projectDropdownRef}>
                    <FormDropdownTrigger
                      $open={projectDropdownOpen}
                      $error={!!errors.project}
                      role="combobox"
                      aria-expanded={projectDropdownOpen}
                      aria-haspopup="listbox"
                      onClick={() => setProjectDropdownOpen(true)}
                    >
                      <div
                        style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}
                      >
                        <DropdownLabel>{tForm("project")}</DropdownLabel>
                        <DropdownInput
                          value={projectDropdownOpen ? projectSearch : selectedProject}
                          onChange={(e) => {
                            setProjectSearch(e.target.value)
                            if (!projectDropdownOpen) setProjectDropdownOpen(true)
                          }}
                          onFocus={() => setProjectDropdownOpen(true)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") setProjectDropdownOpen(false)
                          }}
                          placeholder={tForm("choose")}
                        />
                      </div>
                      <ArrowDown
                        stroke={colors.black}
                        style={{
                          transform: projectDropdownOpen ? "rotate(180deg)" : "none",
                          transition: "transform 0.2s",
                        }}
                      />
                    </FormDropdownTrigger>
                    {projectDropdownOpen && (
                      <FormDropdownMenu role="listbox">
                        {filteredProjects.map((name) => (
                          <FormDropdownItem
                            key={name}
                            role="option"
                            aria-selected={selectedProject === name}
                            tabIndex={0}
                            onClick={() => {
                              setValue("project", name, { shouldValidate: true })
                              setProjectDropdownOpen(false)
                              setProjectSearch("")
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault()
                                setValue("project", name, { shouldValidate: true })
                                setProjectDropdownOpen(false)
                                setProjectSearch("")
                              } else if (e.key === "Escape") {
                                setProjectDropdownOpen(false)
                              }
                            }}
                          >
                            <NewText component="span" desktopFontSize={16} color={colors.black}>
                              {name}
                            </NewText>
                          </FormDropdownItem>
                        ))}
                      </FormDropdownMenu>
                    )}
                  </FormDropdownContainer>
                  <CustomHelperText
                    render={isSubmitted || isSubmitting}
                    error={!!errors.project}
                    text={errors.project?.message}
                  />
                </Grid>
                {/* Room Count */}
                <Grid item xs={12} md={6}>
                  <FormDropdownContainer ref={roomsDropdownRef}>
                    <FormDropdownTrigger
                      $open={roomsDropdownOpen}
                      $error={!!errors.rooms}
                      role="combobox"
                      aria-expanded={roomsDropdownOpen}
                      aria-haspopup="listbox"
                      onClick={() => setRoomsDropdownOpen(true)}
                    >
                      <div
                        style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}
                      >
                        <DropdownLabel>{tForm("rooms")}</DropdownLabel>
                        <DropdownInput
                          value={
                            roomsDropdownOpen
                              ? roomsSearch
                              : selectedRooms.map((r: string) => `${r} ${roomSuffix}`).join(", ")
                          }
                          onChange={(e) => {
                            setRoomsSearch(e.target.value)
                            if (!roomsDropdownOpen) setRoomsDropdownOpen(true)
                          }}
                          onFocus={() => setRoomsDropdownOpen(true)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") setRoomsDropdownOpen(false)
                          }}
                          placeholder={tForm("choose")}
                        />
                      </div>
                      {availableRooms.length > 0 && (
                        <ArrowDown
                          stroke={colors.black}
                          style={{
                            transform: roomsDropdownOpen ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      )}
                    </FormDropdownTrigger>
                    {roomsDropdownOpen && (
                      <FormDropdownMenu role="listbox" aria-multiselectable="true">
                        {filteredRooms.map((room) => (
                          <FormDropdownItem
                            key={room}
                            role="option"
                            aria-selected={(selectedRooms as string[]).includes(room)}
                            tabIndex={0}
                            onClick={() => {
                              const current = selectedRooms as string[]
                              const updated = current.includes(room)
                                ? current.filter((r) => r !== room)
                                : [...current, room]
                              setValue("rooms", updated, { shouldValidate: true })
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault()
                                const current = selectedRooms as string[]
                                const updated = current.includes(room)
                                  ? current.filter((r) => r !== room)
                                  : [...current, room]
                                setValue("rooms", updated, { shouldValidate: true })
                              } else if (e.key === "Escape") {
                                setRoomsDropdownOpen(false)
                              }
                            }}
                          >
                            <Checkbox
                              checked={(selectedRooms as string[]).includes(room)}
                              readOnly
                              icon={<CheckboxIcon size={20} />}
                              checkedIcon={<CheckedCheckboxIcon size={20} />}
                              sx={{ padding: 0 }}
                            />
                            <NewText component="span" desktopFontSize={16} color={colors.black}>
                              {room} {roomSuffix}
                            </NewText>
                          </FormDropdownItem>
                        ))}
                      </FormDropdownMenu>
                    )}
                  </FormDropdownContainer>
                  <CustomHelperText
                    render={isSubmitted || isSubmitting}
                    error={!!errors.rooms}
                    text={errors.rooms?.message}
                  />
                </Grid>
                {/* Entry Date */}
                <Grid item xs={12}>
                  <Controller
                    name="entryDateType"
                    control={control}
                    render={({ field }) => (
                      <EntryDateContainer>
                        <NewText component="p" desktopFontSize={16} color={colors.black}>
                          {tForm("entryDate")}
                        </NewText>
                        <EntryDateToggle role="radiogroup" aria-label={tForm("entryDate")}>
                          {(["immediate", "flexible", "specific"] as const).map((option) => (
                            <EntryDateOption
                              key={option}
                              $active={field.value === option}
                              role="radio"
                              aria-checked={field.value === option}
                              tabIndex={0}
                              onClick={() => field.onChange(option)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault()
                                  field.onChange(option)
                                }
                              }}
                            >
                              {tForm(option)}
                            </EntryDateOption>
                          ))}
                        </EntryDateToggle>
                        <CustomHelperText
                          render={isSubmitted || isSubmitting}
                          error={!!errors.entryDateType}
                          text={errors.entryDateType?.message}
                        />
                      </EntryDateContainer>
                    )}
                  />
                </Grid>
                {/* Specific Date */}
                {entryDateType === "specific" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="specificDate"
                      control={control}
                      render={({ field }) => {
                        return (
                          <LocalizationProvider adapterLocale={locale} dateAdapter={AdapterDayjs}>
                            <DatePicker
                              desktopModeMediaQuery="@media (min-width: 0px)"
                              open={dateOpen}
                              onOpen={() => setDateOpen(true)}
                              onClose={() => setDateOpen(false)}
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(date) => {
                                field.onChange(date ? date.format("YYYY-MM-DD") : "")
                                setDateOpen(false)
                              }}
                              slotProps={{
                                popper: {
                                  anchorEl: () => dateAnchorRef.current!,
                                  modifiers: [
                                    {
                                      name: "sameWidth",
                                      enabled: true,
                                      phase: "beforeWrite",
                                      requires: ["computeStyles"],
                                      fn: ({ state }: any) => {
                                        state.styles.popper.width = `${state.rects.reference.width}px`
                                      },
                                    },
                                  ],
                                  sx: {
                                    zIndex: 100000001,
                                    "& > *": {
                                      maxWidth: "100% !important",
                                      width: "100% !important",
                                    },
                                  },
                                  placement: "bottom-start",
                                },
                                desktopPaper: {
                                  sx: {
                                    width: "100%",
                                    borderRadius: "4px",
                                    border: "1px solid black",
                                    boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.16)",
                                    mt: "4px",
                                    ".MuiDateCalendar-root": {
                                      width: "100% !important",
                                      maxWidth: "100%",
                                    },
                                    ".MuiPickersCalendarHeader-root": {
                                      padding: "0 12px",
                                    },
                                    ".MuiPickersCalendarHeader-label": {
                                      fontSize: 14,
                                      fontWeight: 700,
                                    },
                                    ".MuiDayCalendar-header, .MuiDayCalendar-weekContainer": {
                                      justifyContent: "space-around",
                                    },
                                    ".MuiDayCalendar-weekDayLabel": {
                                      fontSize: 12,
                                      width: 28,
                                      height: 28,
                                      color: "#6f6f6f",
                                    },
                                    ".MuiYearCalendar-root": {
                                      width: "100% !important",
                                    },
                                    ".MuiPickersYear-yearButton": {
                                      fontSize: 12,
                                      height: 28,
                                      width: "auto",
                                      margin: "2px 0",
                                    },
                                    ".MuiPickersDay-root": {
                                      fontSize: 12,
                                      width: 28,
                                      height: 28,
                                      "&.Mui-selected": {
                                        backgroundColor: "#1F375D",
                                        color: "white",
                                        "&:hover": {
                                          backgroundColor: "#1F375D",
                                        },
                                      },
                                      "&:hover": {
                                        backgroundColor: "#f7f7f7",
                                      },
                                    },
                                  },
                                },
                                textField: { sx: { display: "none" } },
                              }}
                            />
                            <FormDropdownTrigger
                              ref={dateAnchorRef}
                              $error={!!errors.specificDate}
                              style={{ cursor: "pointer" }}
                              tabIndex={0}
                              role="button"
                              aria-label={tForm("selectDate")}
                              onClick={() => setDateOpen(true)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault()
                                  setDateOpen(true)
                                }
                              }}
                            >
                              <NewText
                                component="span"
                                desktopFontSize={16}
                                color={field.value ? colors.newBlack : "black"}
                                desktopFontWeight={field.value ? 700 : 300}
                              >
                                {field.value
                                  ? dayjs(field.value).format("DD/MM/YYYY")
                                  : tForm("selectDate")}
                              </NewText>
                              <ArrowDown
                                stroke={colors.black}
                                style={{
                                  transform: dateOpen ? "rotate(180deg)" : "none",
                                  transition: "transform 0.2s",
                                }}
                              />
                            </FormDropdownTrigger>
                            <CustomHelperText
                              render={isSubmitted || isSubmitting}
                              error={!!errors.specificDate}
                              text={errors.specificDate?.message}
                            />
                          </LocalizationProvider>
                        )
                      }}
                    />
                  </Grid>
                )}
                {/* Notes */}
                <Grid item xs={12}>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="filled"
                        label={tForm("notes")}
                        multiline
                        minRows={3}
                        FormHelperTextProps={{
                          sx: { color: "black" },
                        }}
                        inputProps={{
                          maxLength: CHARACTER_LIMIT,
                          style: {
                            fontSize: 16,
                            resize: "vertical",
                          },
                        }}
                        helperText={`${field?.value?.length || 0}/${CHARACTER_LIMIT}`}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                {/* Consents */}
                <Grid item xs={12}>
                  <CheckBoxesContainer>
                    <StyledCheckboxContainer>
                      <StyledCheckbox
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckedCheckboxIcon />}
                        sx={{
                          ":hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                        tabIndex={0}
                        value={checkedMarketing}
                        onChange={() => setCheckedMarketing((el) => !el)}
                      />
                      <NewText
                        desktopFontSize={14}
                        desktopFontWeight="400"
                        desktopLineHeight="20px"
                        mobileFontSize={12}
                        mobileFontWeight="400"
                        mobileLineHeight={"17px"}
                        component="span"
                        color={"black"}
                        maxWidth={"750px"}
                        dangerouslySetInnerHTML={{
                          __html: t("marketingMessage"),
                        }}
                      />
                    </StyledCheckboxContainer>
                    <StyledCheckboxContainer>
                      <StyledCheckbox
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckedCheckboxIcon />}
                        sx={{
                          ":hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                        tabIndex={0}
                        value={checkedCommercial}
                        onChange={() => setCheckedCommercial((el) => !el)}
                      />
                      <NewText
                        desktopFontSize={14}
                        desktopFontWeight="400"
                        desktopLineHeight="20px"
                        mobileFontSize={12}
                        mobileFontWeight="400"
                        mobileLineHeight={"17px"}
                        component="span"
                        color={"black"}
                        maxWidth={"750px"}
                        dangerouslySetInnerHTML={{
                          __html: t("commercialMessage"),
                        }}
                      />
                    </StyledCheckboxContainer>
                  </CheckBoxesContainer>
                  <div style={{ paddingTop: "16px" }}>
                    <NewText
                      desktopFontSize={14}
                      desktopFontWeight="400"
                      desktopLineHeight="20px"
                      mobileFontSize={12}
                      mobileFontWeight="400"
                      mobileLineHeight={"17px"}
                      component="p"
                      color={"black"}
                      maxWidth={"750px"}
                      dangerouslySetInnerHTML={{
                        __html: ctaMessage(t("ctaMessage") || ""),
                      }}
                    />
                  </div>
                </Grid>
              </Grid>
            </StyledInsideForm>
            <BottomContainer>
              <StyledSubmitButton
                type="submit"
                variant="outlined"
                size="large"
                disabled={isSubmitting}
                onClick={() => {
                  submitClick("שליחה", FORM_ELEMENT_NAME)
                }}
              >
                {isSubmitting ? t("sending") : t("send")}
              </StyledSubmitButton>
              {RECAPTCHA_KEY && (
                <ReCAPTCHA
                  ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
                  sitekey={RECAPTCHA_KEY}
                  size="invisible"
                  hl="iw"
                  tabIndex={-1}
                />
              )}
            </BottomContainer>
          </>
        )}
      </StyledForm>
    </FormContainer>
  )
}

export default RentalHousingForm
